import type { DutyBreakdown, DutyType, RegistrationFee, Slab, StampDutyRule } from "./types";

type DutyConfig = {
  dutyType: DutyType;
  ratePercent?: number;
  fixedAmount?: number;
  slabs?: Slab[];
  slabCalculation?: "progressive" | "threshold";
  baseAmount?: number;
  baseUpTo?: number;
  incrementAmount?: number;
  incrementSize?: number;
  minDuty?: number;
  maxDuty?: number;
};

export function formatINR(amount: number): string {
  return `₹${Math.round(amount).toLocaleString("en-IN")}`;
}

function roundRupee(amount: number): number {
  return Math.round(Number.isFinite(amount) ? amount : 0);
}

function formatPercent(percent: number): string {
  return `${Number.isInteger(percent) ? percent : percent.toFixed(6).replace(/\.?0+$/, "")}%`;
}

function ensureValue(input: { value: number }): number {
  return Math.max(0, Number.isFinite(input.value) ? input.value : 0);
}

function computeProgressiveSlabDuty(slabs: Slab[], value: number): { amount: number; steps: string[] } {
  let amount = 0;
  let lowerBound = 0;
  const steps: string[] = [];

  for (const slab of slabs) {
    const upperBound = slab.upTo ?? value;
    const taxablePortion = Math.max(0, Math.min(value, upperBound) - lowerBound);
    const slabLabel = slab.upTo === null
      ? `above ${formatINR(lowerBound)}`
      : `${formatINR(lowerBound)} to ${formatINR(slab.upTo)}`;

    if (taxablePortion > 0 && slab.ratePercent !== undefined) {
      const component = taxablePortion * slab.ratePercent / 100;
      amount += component;
      steps.push(`${formatPercent(slab.ratePercent)} on ${formatINR(taxablePortion)} (${slabLabel}) = ${formatINR(component)}`);
    }

    if (taxablePortion > 0 && slab.fixed !== undefined) {
      amount += slab.fixed;
      steps.push(`Fixed slab amount for ${slabLabel} = ${formatINR(slab.fixed)}`);
    }

    if (taxablePortion > 0 && slab.incrementAmount !== undefined && slab.incrementSize !== undefined) {
      const increments = Math.ceil(taxablePortion / slab.incrementSize);
      const component = increments * slab.incrementAmount;
      amount += component;
      steps.push(`${increments.toLocaleString("en-IN")} increment(s) of ${formatINR(slab.incrementSize)} (${slabLabel}) at ${formatINR(slab.incrementAmount)} each = ${formatINR(component)}`);
    }

    if (slab.upTo === null || value <= upperBound) break;
    lowerBound = upperBound;
  }

  return { amount, steps };
}

function computeThresholdSlabDuty(slabs: Slab[], value: number): { amount: number; steps: string[] } {
  const matchedSlab = slabs.find((slab) => slab.upTo === null || value <= slab.upTo);
  if (!matchedSlab) return { amount: 0, steps: [] };

  const slabLabel = matchedSlab.upTo === null
    ? `above configured threshold`
    : `up to ${formatINR(matchedSlab.upTo)}`;

  if (matchedSlab.ratePercent !== undefined) {
    const amount = value * matchedSlab.ratePercent / 100;
    return {
      amount,
      steps: [`${formatPercent(matchedSlab.ratePercent)} of ${formatINR(value)} (${slabLabel}) = ${formatINR(amount)}`],
    };
  }

  if (matchedSlab.incrementAmount !== undefined && matchedSlab.incrementSize !== undefined) {
    const increments = Math.ceil(value / matchedSlab.incrementSize);
    const amount = increments * matchedSlab.incrementAmount;
    return {
      amount,
      steps: [`${increments.toLocaleString("en-IN")} increment(s) of ${formatINR(matchedSlab.incrementSize)} (${slabLabel}) at ${formatINR(matchedSlab.incrementAmount)} each = ${formatINR(amount)}`],
    };
  }

  const amount = matchedSlab.fixed ?? 0;
  return {
    amount,
    steps: [`Fixed threshold duty (${slabLabel}) = ${formatINR(amount)}`],
  };
}

function computeSlabDuty(config: DutyConfig, value: number): { amount: number; steps: string[] } {
  const slabs = config.slabs ?? [];
  if (config.slabCalculation === "threshold") {
    return computeThresholdSlabDuty(slabs, value);
  }
  return computeProgressiveSlabDuty(slabs, value);
}

function computeIncrementalDuty(config: DutyConfig, value: number): { amount: number; steps: string[] } {
  const baseAmount = config.baseAmount ?? 0;
  const baseUpTo = config.baseUpTo ?? 0;
  const incrementAmount = config.incrementAmount ?? 0;
  const incrementSize = config.incrementSize ?? 1;

  if (value <= baseUpTo) {
    return {
      amount: baseAmount,
      steps: baseUpTo > 0 || baseAmount > 0
        ? [`Fixed duty up to ${formatINR(baseUpTo)} = ${formatINR(baseAmount)}`]
        : [],
    };
  }

  const increments = Math.ceil((value - baseUpTo) / incrementSize);
  const incrementDuty = increments * incrementAmount;
  const steps = baseUpTo > 0 || baseAmount > 0
    ? [`Fixed duty up to ${formatINR(baseUpTo)} = ${formatINR(baseAmount)}`]
    : [];
  steps.push(`${increments.toLocaleString("en-IN")} increment(s) of ${formatINR(incrementSize)} above ${formatINR(baseUpTo)} at ${formatINR(incrementAmount)} each = ${formatINR(incrementDuty)}`);

  return { amount: baseAmount + incrementDuty, steps };
}

function applyFloorAndCap(amount: number, config: DutyConfig, steps: string[]): number {
  let nextAmount = amount;

  if (config.minDuty !== undefined && nextAmount < config.minDuty) {
    steps.push(`Minimum stamp duty applies: ${formatINR(config.minDuty)}`);
    nextAmount = config.minDuty;
  }

  if (config.maxDuty !== undefined && nextAmount > config.maxDuty) {
    steps.push(`Capped at ${formatINR(config.maxDuty)}`);
    nextAmount = config.maxDuty;
  }

  return nextAmount;
}

function computeBaseDuty(config: DutyConfig, value: number): { amount: number; steps: string[] } {
  const steps: string[] = [];
  let amount = 0;

  switch (config.dutyType) {
    case "fixed":
      amount = config.fixedAmount ?? 0;
      steps.push(`Fixed stamp duty = ${formatINR(amount)}`);
      break;

    case "ad_valorem":
      amount = value * (config.ratePercent ?? 0) / 100;
      steps.push(`${formatPercent(config.ratePercent ?? 0)} of ${formatINR(value)} = ${formatINR(amount)}`);
      break;

    case "slab": {
      const slabDuty = computeSlabDuty(config, value);
      amount = slabDuty.amount;
      steps.push(...slabDuty.steps);
      break;
    }

    case "hybrid": {
      if (config.fixedAmount !== undefined) {
        amount += config.fixedAmount;
        steps.push(`Fixed component = ${formatINR(config.fixedAmount)}`);
      }
      if (config.ratePercent !== undefined) {
        const percentageComponent = value * config.ratePercent / 100;
        amount += percentageComponent;
        steps.push(`${formatPercent(config.ratePercent)} of ${formatINR(value)} = ${formatINR(percentageComponent)}`);
      }
      if (config.slabs && config.slabs.length > 0) {
        const slabDuty = computeSlabDuty(config, value);
        amount += slabDuty.amount;
        steps.push(...slabDuty.steps);
      }
      break;
    }

    case "incremental": {
      const incrementalDuty = computeIncrementalDuty(config, value);
      amount = incrementalDuty.amount;
      steps.push(...incrementalDuty.steps);
      break;
    }
  }

  amount = applyFloorAndCap(amount, config, steps);
  return { amount: roundRupee(amount), steps };
}

function computeRegistrationFee(registrationFee: RegistrationFee | undefined, value: number, steps: string[]): number {
  if (!registrationFee) return 0;

  if (registrationFee.type === "fixed") {
    const amount = registrationFee.amount ?? 0;
    steps.push(`Registration fee: fixed ${formatINR(amount)}`);
    return amount;
  }

  let amount = value * (registrationFee.percent ?? 0) / 100;
  let feeStep = `Registration fee: ${formatPercent(registrationFee.percent ?? 0)} of ${formatINR(value)} = ${formatINR(amount)}`;

  if (registrationFee.min !== undefined && amount < registrationFee.min) {
    amount = registrationFee.min;
    feeStep += `; minimum ${formatINR(registrationFee.min)} applies`;
  }

  if (registrationFee.max !== undefined && amount > registrationFee.max) {
    amount = registrationFee.max;
    feeStep += `; capped at ${formatINR(registrationFee.max)}`;
  }

  steps.push(feeStep);
  return amount;
}

export function computeDuty(rule: StampDutyRule, input: { value: number; selectedClauseAddOns?: string[] }): DutyBreakdown {
  const value = ensureValue(input);
  const steps: string[] = [];
  const baseDuty = computeBaseDuty(rule, value);
  const baseStampDuty = baseDuty.amount;
  steps.push(...baseDuty.steps);

  const selectedClauseAddOns = new Set(input.selectedClauseAddOns ?? []);
  const clauseAddOns = (rule.clauseAddOns ?? [])
    .filter((addOn) => selectedClauseAddOns.has(addOn.id))
    .map((addOn) => {
      const addOnDuty = computeBaseDuty(addOn, value);
      steps.push(...addOnDuty.steps.map((step) => `${addOn.label}: ${step}`));
      return {
        id: addOn.id,
        label: addOn.label,
        amount: addOnDuty.amount,
        citation: addOn.citation,
      };
    });

  const surcharges = (rule.surcharges ?? []).map((surcharge) => {
    let amount = surcharge.fixed ?? 0;
    if (surcharge.percent !== undefined) {
      const base = surcharge.appliesTo === "value" ? value : baseStampDuty;
      amount += base * surcharge.percent / 100;
      steps.push(`${surcharge.label}: ${formatPercent(surcharge.percent)} of ${surcharge.appliesTo === "value" ? "value" : "base duty"} (${formatINR(base)}) = ${formatINR(amount)}`);
    } else {
      steps.push(`${surcharge.label}: fixed ${formatINR(amount)}`);
    }
    return { label: surcharge.label, amount: roundRupee(amount) };
  });

  const totalStampDuty = roundRupee(
    baseStampDuty +
    clauseAddOns.reduce((sum, addOn) => sum + addOn.amount, 0) +
    surcharges.reduce((sum, surcharge) => sum + surcharge.amount, 0),
  );
  const registrationFee = roundRupee(computeRegistrationFee(rule.registrationFee, value, steps));
  const totalPayable = roundRupee(totalStampDuty + registrationFee);

  steps.push(`Total stamp duty = ${formatINR(totalStampDuty)}`);
  if (registrationFee > 0) {
    steps.push(`Total payable = ${formatINR(totalStampDuty)} + ${formatINR(registrationFee)} = ${formatINR(totalPayable)}`);
  }

  return {
    baseStampDuty,
    clauseAddOns,
    surcharges,
    totalStampDuty,
    registrationFee,
    totalPayable,
    steps,
  };
}
